import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  InlineStack,
  Select,
  Checkbox,
  Link,
  Card,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { submit } from "./file-form.slice";
import { fetchById, fetchCheckSumFiles } from "services/upload-file.service";
import FileCategorySelect from "components/file-category-select/file-category-select";
import Loading from "components/loading/Loading";
import CustomerSelect from "components/customer-select/CustomerSelect";
import { post } from "services/customers.service";
import _ from "lodash";
import DatePickerx from "features/datepicker/date-picker";
import "./style.scss";
import UserSelect from "features/user-select/user-select";
import MultipleUserSelect from "features/multi-user-select/multi-user-select";
import SearchableSeriesSelect from "components/searchable-series-select/searchable-series-select";
import SearchableFilesTypesSelect from "components/searchable-files-types-select/searchable-files-types-select";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import StickyFormFooter from "components/sticky-form-footer/sticky-form-footer";

function FileForm({
  fileId,
  onChange,
  companyId,
  projectId: ProjectIdProps,
  categoryId,
  parentFileId,
  categoryType,
  subject: subjectProps,
  subjectId: subjectIdProps,
  isCategoryRequired = true,
  isCustomerSelectorVisible,
  customerId: CustomerIdProps,
  isUserSelectorVisible,
  source: sourceProps,
  checksum,
  sanctionsType,
  isVatCheckDocument,
  year,
  customerFinance,
  // Runs after the changes were saved, e.g. so a list outside redux can refresh.
  onSuccess,
}) {
  const dispatch = useDispatch();
  const [id, setId] = useState();
  const [name, setName] = useState("");
  const [categories, setCategories] = useState([]);
  const [dateTo, setDateTo] = useState("");
  const [customer, setCustomer] = useState();
  const [toggleBox, setToggelBox] = useState(false);
  const [relatedTo, setRelatedTo] = useState(null);
  const [date, setDate] = useState();
  const [series, setSeries] = useState("");
  const [assignedUser, setAssignedUser] = useState("");
  const [customFields, setCustomFields] = useState([]);
  const [externalNumber, setExternalNumber] = useState("");
  const [isCustomerOptional, setIsCustomerOptional] = useState(false);
  const [formIsValid, setFormIsValid] = useState(true);
  const [fileType, setFileType] = useState("");
  const [fileTypeContext, setFileTypeContext] = useState("");
  const { customerId, userId, projectId, assetId } = useParams();
  const [users, setUsers] = useState();
  const [projects, setProjects] = useState();
  const [assets, setAssets] = useState();
  const [source, setSource] = useState();
  const [checkSum, setCheckSum] = useState();
  const [existingFiles, setExistingFiles] = useState([]);

  const [visibleInAllProfile, setVisibleInAllProfile] = useState(false);

  const { t } = useTranslation();
  const { isLoading } = useSelector((state) => state.fileFormReducer);
  let location = useLocation();
  const navigate = useNavigate();

  // Error state to store validation errors
  const [errors, setErrors] = useState({
    fileType: "",
    customFields: {},
    category: "",
    assignedUser: "",
    customer: "",
  });

  const handleCheckboxChange = useCallback(
    (setter) => (newChecked) => setter(newChecked),
    []
  );

  useEffect(() => {
    const validationErrors = {
      fileType: "",
      customFields: {},
      category: "",
      assignedUser: "",
    };
    let valid = true;

    // Validate file name [we don't need to required incase of relatedFiles]
    if (!fileType && !relatedTo && !parentFileId) {
      validationErrors.fileType = t("file_type_is_required");
      valid = false;
    }

    // Validate required custom fields
    customFields.forEach((field) => {
      if (field.isRequired && !field.value && field.fieldType != "checkbox") {
        validationErrors.customFields[field.id] = t("field_is_required");
        valid = false;
      }

      // Check minimum length for string and textarea fields
      if (
        field.minLength &&
        field.minLength > 0 &&
        field.value &&
        (field.fieldType === "text" || field.fieldType === "textarea")
      ) {
        if (field.value.length < field.minLength) {
          validationErrors.customFields[field.id] = t(
            "field_must_be_at_least_characters",
            { minLength: field.minLength }
          );
          valid = false;
        }
      }
    });

    // Validate category
    if (!categories.length && isCategoryRequired && !relatedTo) {
      validationErrors.category = t("category_is_required");
      valid = false;
    }

    // Validate assigned user
    if (!assignedUser) {
      validationErrors.assignedUser = t("user_is_required");
      valid = false;
    }

    // customer required check
    if (isCustomerSelectorVisible && !isCustomerOptional && !customer) {
      validationErrors.customer = t("customer_is_required");
      valid = false;
    }

    // date required check
    if (!date) {
      valid = false;
    }

    setErrors(validationErrors);
    setFormIsValid(valid);
  }, [
    fileType,
    customFields,
    assignedUser,
    customer,
    JSON.stringify(categories),
    date,
  ]);

  const handleGetCategoryId = (url) => {
    const urlParts = url.split("/");
    const indexOfDocument = urlParts.indexOf("documents");
    if (indexOfDocument && indexOfDocument + 1 < urlParts.length) {
      return urlParts[indexOfDocument + 1];
    }
    return "";
  };

  const handleFormSubmit = async () => {
    // convert customField data array to an object
    const customizedCustomFieldData = customFields.reduce((acc, field) => {
      acc[field.id] = field["value"];
      return acc;
    }, {});

    const targetCategoryId = handleGetCategoryId(location.pathname);

    const formData = {
      id,
      name,
      categories,
      dateTo,
      customer,
      companyId,
      projectId: ProjectIdProps || projectId,
      assetId: assetId,
      relatedTo: parentFileId || relatedTo,
      date,
      series,
      assignedUser,
      customFields: customizedCustomFieldData,
      externalNumber,
      fileType,
      fileTypeContext,
      customerId: CustomerIdProps || customerId,
      targetCategoryId,
      isCustomerSelectorVisible,
      users,
      userId,
      visibleInAllProfile,
      projects,
      assets,
      source: sourceProps || source,
      checkSum,
      sanctionsType,
      isVatCheckDocument,
      year,
      ...(customerFinance && { customerFinance }),
    };

    const saved = await dispatch(submit(formData));
    if (saved) onSuccess?.();
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const {
          name,
          categories,
          dateTo,
          relatedTo,
          date,
          assignedUser,
          customFields,
          series,
          externalNumber,
          customer,
          fileType,
          fileTypeContext,
          users,
          visibleInAllProfile,
          projects,
          assets,
          source,
        } = await fetchById(fileId, companyId);
        setName(name);
        setDateTo(dateTo);
        setRelatedTo(relatedTo?.id);
        setDate(date);
        setSeries(series?.id);
        setExternalNumber(externalNumber);
        setAssignedUser(assignedUser?.id);
        setCustomFields(customFields);
        setFileType(fileType);
        setFileTypeContext(fileTypeContext);
        setVisibleInAllProfile(visibleInAllProfile);
        setSource(source);
        categoryId
          ? setCategories([categoryId])
          : setCategories(
              categories.map((categoryEntity) => categoryEntity.id)
            );
        setCustomer(customer.map((customerEntity) => customerEntity.id));
        setUsers(users.map((userEntity) => userEntity.id));
        setProjects(projects.map((projectEntity) => projectEntity.id));
        setAssets(assets.map((assetEntity) => assetEntity.id));
        if (subjectProps === "customer") {
          if (subjectIdProps && !customer.length) setCustomer([subjectIdProps]);
        }
        if (subjectProps === "user") {
          if (subjectIdProps && !users.length) setUsers([subjectIdProps]);
        }
        if (subjectProps === "project") {
          if (subjectIdProps && !projects.length) setProjects([subjectIdProps]);
        }
        if (subjectProps === "asset") {
          if (subjectIdProps && !assets.length) setAssets([subjectIdProps]);
        }
        if (checksum) {
          setCheckSum(checksum);
        }
      } catch (e) {
        // console.error(e);
      }
    }
    if (fileId) {
      setId(fileId);
      fetchData();
    }
  }, [fileId, companyId, categoryId, isCustomerSelectorVisible]);

  useEffect(() => {
    // Follow the category both ways. This only ever set true, so after viewing
    // an internal category the flag stayed on — and an external document (a
    // contract, an incoming letter) could then be saved with no counterparty.
    setIsCustomerOptional(categoryType === "internal");
  }, [categoryType]);

  useEffect(() => {
    async function fetchData() {
      try {
        const existingCheckSumFiles = await fetchCheckSumFiles(
          fileId,
          companyId
        );
        const filesEntities = [];
        existingCheckSumFiles.map((fileEntity) => {
          const fileid = fileEntity.id;
          const categoryId = fileEntity.filesToCategories.length
            ? fileEntity.filesToCategories[0].filescategories
            : null;
          if (categoryId) {
            const url = `/app/${companyId}/profile/files/${categoryId}/${fileid}`;
            const name = fileEntity.name || fileEntity.title;
            filesEntities.push({ url, name });
          } else {
            const name = fileEntity.name || fileEntity.title;
            filesEntities.push({ url: "", name });
          }
        });
        setExistingFiles(filesEntities);
      } catch (e) {
        // console.error(e);
      }
    }

    if (checksum) {
      fetchData();
    }
  }, [checksum]);

  if (isLoading) {
    return <Loading />;
  }

  const handleSwitchIpBox = () => {
    setToggelBox(!toggleBox);
    setCustomer();
  };

  const handleCreateCustomer = async () => {
    if (toggleBox) {
      const formData = {
        name: customer,
        companyId,
        address: "",
        bankIban: "",
        bankName: "",
        companyCode: "",
        contactPerson: "",
        customerId: "",
        email: "",
        fine: "",
        maxAllowedDebt: "",
        paymentDelay: "",
        phone: "",
        prepaymentPercentage: "",
        vatCode: "",
      };
      const result = await post(companyId, formData);
      setToggelBox(!toggleBox);
      setCustomer(_.get(result, "createdCustomer.id"));
    }
  };

  const handleDueDateChange = (value) => setDateTo(value);

  const handleCustomField = (value, id) => {
    setCustomFields((prevFields) =>
      prevFields.map((field) => (field.id === id ? { ...field, value } : field))
    );
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <div className="user-form">
      {existingFiles && existingFiles.length > 0 && (
        <Card background="bg-fill-caution-active" roundedAbove="sm">
          <Text variant="headingMd" as="h4">
            {t("checksum_files")}
          </Text>
          {existingFiles.map((fileNavigationEntity) => {
            return (
              <div>
                <Link url={fileNavigationEntity.url}>
                  {fileNavigationEntity.name}
                </Link>
              </div>
            );
          })}
        </Card>
      )}
      <Form className="user-form_form" onSubmit={handleFormSubmit}>
        <FormLayout>
          {/* file type will be hide incase of child file upload/edit */}
          {!relatedTo && !parentFileId && (
            <SearchableFilesTypesSelect
              isRequired={relatedTo || parentFileId ? false : true}
              onChange={setFileType}
              value={fileType}
            />
          )}
          <TextField
            disabled={isLoading}
            label={t("file_context")}
            type="text"
            autoComplete="off"
            value={fileTypeContext}
            onChange={setFileTypeContext}
            requiredIndicator={true}
            // error={errors.context}
            labelHidden
            placeholder={t("enter_file_context")}
          />
          {/* category will be hide incase of child file upload/edit */}
          {!relatedTo && isCategoryRequired && (
            <FileCategorySelect
              onChange={setCategories}
              value={categories}
              isRequired={true}
              allowMultiple={true}
            />
          )}
          {/* this section will be visible if user wants to upload file for customer */}
          {isCustomerSelectorVisible && (
            <>
              {!toggleBox && (
                <>
                  <span onClick={handleSwitchIpBox}>
                    <Button>{t("create_customer")}</Button>
                  </span>
                  <CustomerSelect
                    value={customer}
                    onChange={setCustomer}
                    isRequired={false}
                    allowMultiple={true}
                  />
                </>
              )}
              {toggleBox && (
                <>
                  <span onClick={handleSwitchIpBox}>
                    <Button>{t("select_existing_customer")}</Button>
                  </span>
                  <TextField
                    label={t("customer_name")}
                    value={customer}
                    onChange={setCustomer}
                    autoComplete="off"
                    requiredIndicator={isCustomerOptional ? false : true}
                    error={
                      isCustomerSelectorVisible &&
                      !customer &&
                      !isCustomerOptional
                        ? t("field_is_required")
                        : ""
                    }
                  />
                  <div className="marginTop">
                    <Button
                      loading={isLoading}
                      disabled={!customer}
                      onClick={handleCreateCustomer}
                    >
                      {t("save_customer")}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          <SearchableSeriesSelect onChange={setSeries} value={series} />
          {/* this section will be visible if user wants to attach multiple user to a file */}
          {isUserSelectorVisible && (
            <MultipleUserSelect
              onChange={setUsers}
              value={users}
              label={"selected_user"}
              isUserGuest={false}
            />
          )}

          <UserSelect
            onChange={setAssignedUser}
            value={assignedUser}
            isRequired={true}
            label={"Select assigned user"}
          />
          <TextField
            label={t("externalNumber")}
            value={externalNumber}
            onChange={setExternalNumber}
            autoComplete="off"
          />
          <InlineStack align="space-between">
            <DatePickerx
              name="date"
              title={t("document_date")}
              onChange={setDate}
              selected={date}
              isRequired={true}
            />
            <DatePickerx
              name="dueDate"
              title={t("dateTo")}
              onChange={handleDueDateChange}
              selected={dateTo}
            />
          </InlineStack>
          {customFields && (
            <div className="column-2">
              {customFields?.map((field) => {
                if (
                  !field.fieldType ||
                  !field.isVisibleInList ||
                  field.fieldType === "checkbox"
                )
                  return null;
                return (
                  <div className="w-full md:w-[48%]">
                    {field.fieldType === "select" && (
                      <Select
                        label={field.label}
                        options={[
                          { label: "-", value: "" },
                          ...field.fieldOptions.map((option) => ({
                            ...option,
                            key: `option_${option.label}`,
                          })),
                        ]}
                        onChange={(value) => handleCustomField(value, field.id)}
                        value={field.value}
                        error={errors.customFields[field.id]}
                        requiredIndicator={field.isRequired}
                      />
                    )}
                    {field.fieldType === "date" && (
                      <DatePickerx
                        name="date"
                        title={field.label}
                        onChange={(value) => handleCustomField(value, field.id)}
                        selected={field.value}
                        isRequired={field.isRequired ? true : false}
                      />
                    )}
                    {(field.fieldType === "text" ||
                      field.fieldType === "number") && (
                      <TextField
                        type={field.fieldType}
                        key={field.id}
                        label={field.label}
                        value={field.value}
                        onChange={(value) => handleCustomField(value, field.id)}
                        autoComplete="off"
                        requiredIndicator={field.isRequired}
                        error={errors.customFields[field.id]}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {customFields && (
            <div className="column-2">
              {customFields.map((field) => {
                if (
                  !field.fieldType ||
                  field.fieldType !== "checkbox" ||
                  !field.isVisibleInList
                )
                  return null;
                return (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "20px",
                    }}
                    key={field.id}
                  >
                    <Checkbox
                      key={field.label}
                      label={field.label}
                      checked={field.value == "true" ? true : false}
                      onChange={() => {
                        const value = field.value == "true" ? "false" : "true";
                        handleCustomField(value, field.id);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <Checkbox
            label={t("visible_in_all_profile")}
            checked={visibleInAllProfile}
            onChange={handleCheckboxChange(setVisibleInAllProfile)}
          />

        </FormLayout>
        <StickyFormFooter>
          <Button
            loading={isLoading}
            disabled={!formIsValid || toggleBox}
            submit
          >
            {t("save")}
          </Button>
        </StickyFormFooter>
      </Form>
    </div>
  );
}

export default FileForm;
